import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Field, Notice } from '../../components/ui/misc'
import { api, HttpError, useConfig } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { AuthLayout, Divider, GoogleButton, authErrorMessage } from './AuthLayout'
import { safeNext } from './Login'

/**
 * /signup and /waitlist. While the beta is invite-only this is the waitlist form: no account, no password, no
 * verification, just an email saying you're on the list. Creating an account is for people let in: their approval
 * email links here with ?email=, organization invitations with ?next=/invite/…, and anyone once sign-up is open.
 * The server enforces who may create an account (services/onboarding.ts#signupGate); this page only routes people.
 */
export function SignupPage() {
  const [params] = useSearchParams()
  const { data: config, isLoading } = useConfig()
  const next = safeNext(params.get('next'))
  const beta = config?.signupMode !== 'open'
  // let in already: the approval email (?email=) or an organization invitation; Google refusals come back with ?create
  const [create] = useState(params.has('email') || params.has('create') || next.startsWith('/invite/'))

  if (isLoading) return null
  return beta && !create ? <Waitlist /> : <CreateAccount beta={beta} next={next} />
}

function Waitlist() {
  const [form, setForm] = useState({ name: '', email: '', company: '', useCase: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [joined, setJoined] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/api/waitlist', {
        body: {
          email: form.email,
          name: form.name || undefined,
          company: form.company || undefined,
          useCase: form.useCase || undefined,
        },
      })
      setJoined(true)
    } catch (err) {
      setError(err instanceof HttpError && err.status === 429 ? 'Too many tries from here. Try again in a little while.' : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  if (joined)
    return (
      <AuthLayout title="You're on the list" subtitle={`We sent a confirmation to ${form.email}.`}>
        <p className="text-[13px] leading-relaxed text-ink-secondary">
          We're reviewing applications and will reach out as soon as we can. When you're in, we'll email you a link to create your account.
        </p>
        <p className="mt-3 text-[12px] text-ink-muted">Invited by a teammate? Use the link in their invitation: it skips the wait.</p>
      </AuthLayout>
    )

  return (
    <AuthLayout
      title="Join the waitlist"
      subtitle="We're in private beta. Tell us a little about what you're building and we'll be in touch as soon as we can."
      footer={
        <>
          Already have an account?{' '}
          <Link className="font-medium text-brand" to="/login">
            Sign in
          </Link>
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <Notice tone="warning">{error}</Notice>
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name">
          <input className="input" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Work email">
          <input
            className="input"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Company" hint="Optional.">
          <input className="input" autoComplete="organization" maxLength={120} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </Field>
        <Field label="What will you use it for?" hint="Optional. Evals, routing, guardrails, tool choice… a sentence is plenty.">
          <textarea className="input min-h-[72px]" maxLength={2000} value={form.useCase} onChange={(e) => setForm({ ...form, useCase: e.target.value })} />
        </Field>
        <Button type="submit" className="h-10 w-full" loading={busy}>
          Join the waitlist
        </Button>
      </form>
    </AuthLayout>
  )
}

function CreateAccount({ beta, next }: { beta: boolean; next: string }) {
  const [params] = useSearchParams()
  const { data: config } = useConfig()
  const [name, setName] = useState('')
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  // Google sends a refused sign-up back here with ?error=
  const [error, setError] = useState<string | null>(() => {
    const code = params.get('error')
    return code ? authErrorMessage({ code, message: code }) : null
  })
  const [sent, setSent] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const { error: err } = await authClient.signUp.email({ name, email, password, callbackURL: next })
    setBusy(false)
    if (err) return setError(authErrorMessage(err))
    setSent(true)
  }

  if (sent)
    return (
      <AuthLayout title="Check your email" subtitle={`We sent a verification link to ${email}.`}>
        <p className="text-[13px] text-ink-secondary">Click the link to finish creating your account. It expires in 24 hours.</p>
        <p className="mt-3 text-[12px] text-ink-muted">Nothing there? Check spam, or sign in to have it sent again.</p>
      </AuthLayout>
    )

  return (
    <AuthLayout
      title="Create your account"
      subtitle={
        beta ? 'Use the email address your invitation or approval went to.' : 'Get an API key in a minute.'
      }
      footer={
        <>
          Have an account?{' '}
          <Link className="font-medium text-brand" to={`/login?next=${encodeURIComponent(next)}`}>
            Sign in
          </Link>
          {beta && (
            <>
              {' · '}
              <Link className="font-medium text-brand" to="/waitlist">
                Join the waitlist
              </Link>
            </>
          )}
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <Notice tone="warning">{error}</Notice>
        </div>
      )}
      {config?.features.google && (
        <>
          <GoogleButton
            label="Sign up with Google"
            onClick={() =>
              authClient.signIn.social({ provider: 'google', callbackURL: next, errorCallbackURL: `/signup?create&next=${encodeURIComponent(next)}` })
            }
          />
          <Divider />
        </>
      )}
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name">
          <input className="input" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Work email">
          <input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" hint="At least 10 characters.">
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" className="h-10 w-full" loading={busy}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  )
}
